import type { IntlService } from 'ember-intl';
import { deepMerge } from './deep-merge';

export interface LoadTranslationsOptions {
  intl: IntlService;
  locale: string;
  appTranslationsImport: Promise<{ default: Record<string, unknown> }>;
  commonTranslationsImport: Promise<{ default: Record<string, unknown> }>;
}

export interface LoadTranslationsWithFallbackOptions {
  intl: IntlService;
  locale: string;
  fallbackLocale?: string;
  appTranslationsImport: Promise<{ default: Record<string, unknown> }>;
  commonTranslationsImport: Promise<{ default: Record<string, unknown> }>;
  // Fallback imports (optional - only needed when locale !== fallbackLocale)
  fallbackAppTranslationsImport?: Promise<{ default: Record<string, unknown> }>;
  fallbackCommonTranslationsImport?: Promise<{ default: Record<string, unknown> }>;
}

/**
 * Load and merge app and common translations, then add them to the intl service
 * @example
 * await loadTranslations({
 *   intl: this.intl,
 *   locale: 'en-us',
 *   appTranslationsImport: import('../../translations/en-us.yaml'),
 *   commonTranslationsImport: import('tio-common/translations/en-us.yaml')
 * });
 */
export async function loadTranslations({
  intl,
  locale,
  appTranslationsImport,
  commonTranslationsImport,
}: LoadTranslationsOptions): Promise<void> {
  const [appModule, commonModule] = await Promise.all([
    appTranslationsImport,
    commonTranslationsImport,
  ]);

  const appTranslations = appModule.default;
  const commonTranslations = commonModule.default;

  const combinedTranslations = deepMerge({}, commonTranslations, appTranslations);

  intl.addTranslations(locale, combinedTranslations);
}

/**
 * Load translations with fallback support for incremental translation.
 *
 * Loads translations in this order (later overwrites earlier):
 * 1. Fallback common translations (e.g., tio-common/en-us.yaml)
 * 2. Fallback app translations (e.g., tio-admin/en-us.yaml)
 * 3. Target common translations (e.g., tio-common/fr-ca.yaml)
 * 4. Target app translations (e.g., tio-admin/fr-ca.yaml)
 *
 * This ensures missing keys in the target locale gracefully fall back to English.
 *
 * @example
 * await loadTranslationsWithFallback({
 *   intl: this.intl,
 *   locale: 'fr-ca',
 *   fallbackLocale: 'en-us',
 *   appTranslationsImport: import('../../translations/fr-ca.yaml'),
 *   commonTranslationsImport: import('tio-common/translations/fr-ca.yaml'),
 *   fallbackAppTranslationsImport: import('../../translations/en-us.yaml'),
 *   fallbackCommonTranslationsImport: import('tio-common/translations/en-us.yaml'),
 * });
 */
export async function loadTranslationsWithFallback({
  intl,
  locale,
  fallbackLocale = 'en-us',
  appTranslationsImport,
  commonTranslationsImport,
  fallbackAppTranslationsImport,
  fallbackCommonTranslationsImport,
}: LoadTranslationsWithFallbackOptions): Promise<void> {
  // If locale is the fallback, just use the standard loader
  if (
    locale === fallbackLocale ||
    !fallbackAppTranslationsImport ||
    !fallbackCommonTranslationsImport
  ) {
    return loadTranslations({
      intl,
      locale,
      appTranslationsImport,
      commonTranslationsImport,
    });
  }

  // Load all translations in parallel
  const [fallbackAppModule, fallbackCommonModule, appModule, commonModule] = await Promise.all([
    fallbackAppTranslationsImport,
    fallbackCommonTranslationsImport,
    appTranslationsImport,
    commonTranslationsImport,
  ]);

  // Merge in order: fallback common -> fallback app -> target common -> target app
  // Later values overwrite earlier ones, so target locale takes precedence
  const combinedTranslations = deepMerge(
    {},
    fallbackCommonModule.default,
    fallbackAppModule.default,
    commonModule.default,
    appModule.default
  );

  intl.addTranslations(locale, combinedTranslations);
}
